import { useEffect, useState, useMemo, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Flag, Search, MessageSquare, Copy, Download, RefreshCw, Trash2, Bot, User,
  Clock, Filter, Sparkles, X, ArrowDownWideNarrow,
} from "lucide-react";

const REF_REGEX = /NEXON-[A-Z0-9]{4,}-[A-Z0-9]{4}/gi;

// Intent detection from message content
function detectIntent(text) {
  if (!text) return null;
  const t = text.toLowerCase();
  if (/rate|percentage|how much|payout for|how many/.test(t)) return "rate_check";
  if (/status|where is|when will|update on|track/.test(t)) return "status_check";
  if (/human|agent|manager|supervisor|real person/.test(t)) return "handoff_request";
  if (/refund|dispute|complaint|angry|scam/.test(t)) return "dispute";
  if (/how (do|to) (i|submit|sell)|first time|new here/.test(t)) return "how_to_submit";
  if (/thanks|thank you|great|awesome|appreciate/.test(t)) return "positive";
  return null;
}

const INTENT_STYLES = {
  rate_check:      { label: "RATE CHECK",   cls: "bg-sky-100 text-sky-700" },
  status_check:    { label: "STATUS",       cls: "bg-amber-100 text-amber-700" },
  handoff_request: { label: "HANDOFF",      cls: "bg-rose-100 text-rose-700" },
  dispute:         { label: "DISPUTE",      cls: "bg-purple-100 text-purple-700" },
  how_to_submit:   { label: "ONBOARDING",   cls: "bg-emerald-100 text-emerald-700" },
  positive:        { label: "POSITIVE",     cls: "bg-lime-100 text-lime-700" },
};

// Sentiment guess from full transcript
function sentimentFor(msgs) {
  const text = msgs.filter((m) => m.role === "user").map((m) => m.text || "").join(" ").toLowerCase();
  if (/angry|scam|fraud|refund|complain|terrible|awful|ripped|steal/.test(text)) return { key: "angry", label: "FRUSTRATED", cls: "bg-rose-500 text-white" };
  if (/thanks|thank|great|awesome|love|amazing/.test(text)) return { key: "happy", label: "HAPPY", cls: "bg-emerald-500 text-white" };
  if (/wait|when|still|delay|hours ago/.test(text)) return { key: "impatient", label: "IMPATIENT", cls: "bg-amber-500 text-white" };
  return { key: "calm", label: "CALM", cls: "bg-slate-200 text-slate-700" };
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}
function fmtTime(iso) { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
function sameDay(a, b) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function HighlightedText({ text, query }) {
  if (!text) return null;
  // Linkify NEXON refs
  const nodes = [];
  let last = 0, m;
  const re = /NEXON-[A-Z0-9]{4,}-[A-Z0-9]{4}/gi;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(<Link key={`r${m.index}`} to={`/admin/submissions?q=${encodeURIComponent(m[0])}`} className="font-mono font-black text-sky-600 hover:text-sky-800 hover:underline">{m[0]}</Link>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  // Highlight query matches
  if (!query) return <>{nodes.map((n, i) => typeof n === "string" ? <span key={i}>{n}</span> : n)}</>;
  const q = query.toLowerCase();
  const wrap = (s) => {
    if (typeof s !== "string") return s;
    const idx = s.toLowerCase().indexOf(q);
    if (idx === -1) return s;
    return (
      <>
        {s.slice(0, idx)}
        <mark className="bg-yellow-200 rounded px-0.5">{s.slice(idx, idx + q.length)}</mark>
        {s.slice(idx + q.length)}
      </>
    );
  };
  return <>{nodes.map((n, i) => <span key={i}>{wrap(n)}</span>)}</>;
}

const FILTERS = [
  { v: "all", label: "All", tone: "slate" },
  { v: "handoff", label: "Escalated", tone: "rose" },
  { v: "long", label: "Long chats", tone: "fuchsia" },
  { v: "recent", label: "Last 24h", tone: "emerald" },
];

const SORTS = [
  { v: "recent", label: "Most recent" },
  { v: "longest", label: "Longest" },
  { v: "oldest", label: "Oldest" },
];

export default function AdminAiTranscripts() {
  const [sessions, setSessions] = useState([]);
  const [messagesBySid, setMessagesBySid] = useState({}); // preload counts per session
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [msgQ, setMsgQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/chat-sessions");
      const leo = (r.data || []).filter((s) => s.channel === "leo" || !s.channel);
      // fetch message counts in parallel (limit 30 to avoid over-fetching)
      const withCounts = await Promise.all(leo.slice(0, 100).map(async (s) => {
        try {
          const m = await adminApi.get(`/admin/chat-sessions/${s.session_id}/messages`);
          const preview = m.data.slice(-1)[0]?.text || "";
          const first = m.data[0]?.text || "";
          return { ...s, msg_count: m.data.length, last_msg: preview, first_msg: first, all_msgs: m.data };
        } catch { return { ...s, msg_count: 0, last_msg: "", first_msg: "" }; }
      }));
      setSessions(withCounts);
    } catch { toast.error("Failed to load sessions"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSession = async (s) => {
    setSelected(s);
    try {
      const { data } = await adminApi.get(`/admin/chat-sessions/${s.session_id}/messages`);
      setMessages(data);
    } catch { /* */ }
  };

  const filtered = useMemo(() => {
    let list = [...sessions];
    // filter
    if (filter === "handoff") list = list.filter((s) => s.status === "handoff");
    else if (filter === "long") list = list.filter((s) => (s.msg_count || 0) >= 6);
    else if (filter === "recent") {
      const cut = Date.now() - 24 * 3600 * 1000;
      list = list.filter((s) => new Date(s.updated_at || s.created_at).getTime() >= cut);
    }
    // search
    if (q) {
      const query = q.toLowerCase();
      list = list.filter((s) =>
        (s.session_id || "").toLowerCase().includes(query) ||
        (s.customer_email || "").toLowerCase().includes(query) ||
        (s.last_msg || "").toLowerCase().includes(query) ||
        (s.first_msg || "").toLowerCase().includes(query),
      );
    }
    // sort
    if (sort === "longest") list.sort((a, b) => (b.msg_count || 0) - (a.msg_count || 0));
    else if (sort === "oldest") list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else list.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    return list;
  }, [sessions, filter, q, sort]);

  const stats = useMemo(() => {
    const total = sessions.length;
    const handoff = sessions.filter((s) => s.status === "handoff").length;
    const avgMsgs = total ? Math.round(sessions.reduce((a, s) => a + (s.msg_count || 0), 0) / total) : 0;
    const day = Date.now() - 24 * 3600 * 1000;
    const last24 = sessions.filter((s) => new Date(s.updated_at || s.created_at).getTime() >= day).length;
    return { total, handoff, avgMsgs, last24 };
  }, [sessions]);

  const copyTranscript = () => {
    const t = messages.map((m) => `[${new Date(m.created_at).toLocaleString()}] ${m.role === "user" ? "Customer" : "Miles"}: ${m.text}`).join("\n");
    navigator.clipboard?.writeText(t);
    toast.success("Transcript copied");
  };
  const exportSession = () => {
    const csv = "role,author,text,created_at\n" + messages.map((m) => `"${m.role}","${m.author || ""}","${(m.text || "").replace(/"/g, '""')}","${m.created_at}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `transcript-${selected.session_id}.csv`; a.click();
  };

  const sentiment = selected ? sentimentFor(messages) : null;
  const firstUserMsg = messages.find((m) => m.role === "user");
  const intent = firstUserMsg ? detectIntent(firstUserMsg.text) : null;
  const userMsgs = messages.filter((m) => m.role === "user").length;
  const botMsgs = messages.filter((m) => m.role !== "user").length;

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-ai-transcripts">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">All chats</span>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1" data-testid="ai-transcripts-title">Chat transcripts</h1>
            <p className="text-slate-500 mt-1 text-sm">Every conversation Miles has had · read-only</p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStat label="TOTAL CHATS" value={stats.total} icon={MessageSquare} tone="slate" />
          <MiniStat label="ESCALATED" value={stats.handoff} icon={Flag} tone="rose" />
          <MiniStat label="LAST 24H" value={stats.last24} icon={Clock} tone="emerald" />
          <MiniStat label="AVG MESSAGES" value={stats.avgMsgs} icon={Sparkles} tone="fuchsia" />
        </div>

        {/* Toolbar */}
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm p-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search session id, email, or message text…" data-testid="ai-search"
              className="w-full h-10 pl-10 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            {FILTERS.map((f) => (
              <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`ai-filter-${f.v}`}
                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-black transition ${filter === f.v ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>
                <Filter size={11} /> {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="ai-sort"
              className="appearance-none h-10 pl-9 pr-8 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
            <ArrowDownWideNarrow size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <button onClick={load} disabled={loading} data-testid="ai-refresh"
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold disabled:opacity-50">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* 2-column console */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[360px,1fr] min-h-[calc(100vh-380px)]">
            {/* Sessions list */}
            <aside className="border-r border-slate-100 bg-slate-50/40 overflow-y-auto max-h-[calc(100vh-380px)]">
              {filtered.length === 0 && <div className="p-12 text-center text-sm text-slate-400">No sessions match your filters.</div>}
              {filtered.map((s) => {
                const active = selected?.session_id === s.session_id;
                const intent = detectIntent(s.first_msg);
                const intentStyle = intent && INTENT_STYLES[intent];
                return (
                  <button key={s.session_id} onClick={() => openSession(s)}
                    data-testid={`ai-session-${s.session_id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 transition ${
                      active ? "bg-white border-l-4 border-l-indigo-500" : "hover:bg-white"
                    }`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-mono text-[11px] font-black text-slate-500">{s.session_id.slice(0, 16)}…</span>
                      <span className="text-[10px] text-slate-400">{fmtRelative(s.updated_at || s.created_at)}</span>
                    </div>
                    <div className="text-xs text-slate-700 line-clamp-2 leading-snug">{s.last_msg || "No messages"}</div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-slate-100 text-slate-600">
                        <MessageSquare size={9} /> {s.msg_count || 0}
                      </span>
                      {intentStyle && <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-black ${intentStyle.cls}`}>{intentStyle.label}</span>}
                      {s.status === "handoff" && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-rose-500 text-white"><Flag size={8} /> ESCALATED</span>}
                      {s.customer_email && <span className="text-[10px] text-indigo-600 font-bold truncate max-w-[140px]">{s.customer_email}</span>}
                    </div>
                  </button>
                );
              })}
            </aside>

            {/* Transcript viewer */}
            <section className="flex flex-col">
              {!selected && (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3 py-24">
                  <MessageSquare size={40} className="text-slate-200" />
                  <div className="text-sm">Select a session to view the transcript</div>
                </div>
              )}
              {selected && (
                <>
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-500 font-black">{selected.session_id}</span>
                        {sentiment && <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black tracking-wide ${sentiment.cls}`}>{sentiment.label}</span>}
                        {intent && INTENT_STYLES[intent] && <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-black ${INTENT_STYLES[intent].cls}`}>{INTENT_STYLES[intent].label}</span>}
                        {selected.status === "handoff" && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black bg-rose-500 text-white"><Flag size={9} /> ESCALATED</span>}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1"><User size={11} /> {userMsgs}</span>
                        <span className="flex items-center gap-1"><Bot size={11} /> {botMsgs}</span>
                        <span>·</span>
                        <span>Started {fmtRelative(selected.created_at)}</span>
                        {selected.customer_email && <><span>·</span><span className="text-indigo-600 font-bold">{selected.customer_email}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="relative">
                        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input value={msgQ} onChange={(e) => setMsgQ(e.target.value)} placeholder="Search this transcript…" data-testid="ai-transcript-search"
                          className="pl-7 pr-2 h-8 rounded-lg border border-slate-200 text-xs w-40 focus:outline-none focus:border-indigo-400" />
                        {msgQ && <button onClick={() => setMsgQ("")} className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"><X size={10} /></button>}
                      </div>
                      <button onClick={copyTranscript} title="Copy transcript" data-testid="ai-copy"
                        className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Copy size={13} /></button>
                      <button onClick={exportSession} title="Export CSV" data-testid="ai-export"
                        className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Download size={13} /></button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/40 max-h-[calc(100vh-500px)]">
                    {messages.map((m, i) => {
                      const isUser = m.role === "user";
                      const isSystem = m.role === "system";
                      const showDay = i === 0 || !sameDay(messages[i - 1].created_at, m.created_at);
                      return (
                        <div key={m.id || i}>
                          {showDay && (
                            <div className="flex items-center gap-3 my-4">
                              <div className="flex-1 h-px bg-slate-200" />
                              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                {new Date(m.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                              </span>
                              <div className="flex-1 h-px bg-slate-200" />
                            </div>
                          )}
                          <div className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                              isSystem ? "bg-amber-50 text-amber-800 text-xs border border-amber-200 mx-auto !max-w-full text-center italic"
                                : isUser ? "bg-white border border-slate-200 text-slate-900 rounded-tl-none"
                                : "bg-gradient-to-br from-sky-500 to-fuchsia-500 text-white rounded-tr-none"
                            }`}>
                              {!isSystem && !isUser && <div className="text-[10px] font-black opacity-80 mb-0.5 uppercase tracking-wider">Miles</div>}
                              <HighlightedText text={m.text} query={msgQ} />
                              <div className={`text-[9px] mt-1 opacity-70 ${isUser ? "text-slate-500" : "text-white/80"}`}>{fmtTime(m.created_at)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {!messages.length && <div className="text-center text-xs text-slate-400 py-16">No messages in this session.</div>}
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function MiniStat({ label, value, icon: Icon, tone }) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    rose: "bg-rose-100 text-rose-700",
    emerald: "bg-emerald-100 text-emerald-700",
    fuchsia: "bg-indigo-100 text-indigo-700",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${map}`}><Icon size={15} strokeWidth={2.5} /></div>
      <div>
        <div className="text-2xl font-black text-slate-900 tabular-nums">{value}</div>
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</div>
      </div>
    </div>
  );
}
